import React from 'react';
import { Link } from 'react-router-dom';
import { Seo } from '../../components/Seo';
import { keywordsFlow, keywordsPay } from '../../seo/keywords';
import { breadcrumbJsonLd } from '../../seo/site';

const BackOffice: React.FC = () => {
  return (
    <div className="pt-24">
      <Seo
        title="Back-Office Data Management | Invoice Data Entry"
        description="Back-office data management: Cimmple enters invoices and order documents into CimmpleFlow machine shop ERP and CimmplePay payroll software."
        keywords={['back-office data management', 'invoice data entry', ...keywordsFlow, ...keywordsPay]}
        jsonLd={breadcrumbJsonLd([
          { name: 'Home', path: '/' },
          { name: 'Services', path: '/services' },
          { name: 'Back-Office Data Management', path: '/services/back-office' },
        ])}
      />
      {/* Hero Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="mb-8">
            <ol className="flex items-center gap-2 text-sm text-slate-500 dark:text-dark-400">
              <li><Link to="/" className="hover:text-slate-900 dark:hover:text-white transition-colors">Home</Link></li>
              <li>/</li>
              <li><Link to="/services" className="hover:text-slate-900 dark:hover:text-white transition-colors">Services</Link></li>
              <li>/</li>
              <li className="text-slate-900 dark:text-white font-medium">Back-Office Data Management</li>
            </ol>
          </nav>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="w-24 h-24 rounded-3xl bg-primary-100 dark:bg-primary-500/10 text-primary-600 dark:text-primary-400 flex items-center justify-center mb-8">
                <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h1 className="text-4xl md:text-5xl font-display font-bold text-slate-900 dark:text-white mb-4">
                Back-Office Data Management
              </h1>
              <p className="text-xl text-primary-600 dark:text-primary-400 font-medium mb-6">
                We handle the data so you can focus on your business
              </p>
              <p className="text-slate-600 dark:text-dark-300 text-lg whitespace-pre-line">
                Our back-office team receives your invoices and order documents by email—including PDFs—and enters all data into your CimmpleFlow and/or CimmplePay subscription. Your records stay up to date without you spending time on data entry.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 mt-8">
                <Link to="/contact" className="btn-primary">
                  Get a Quote
                </Link>
                <Link to="/products" className="btn-secondary">
                  View CimmpleFlow & CimmplePay
                </Link>
              </div>
            </div>

            <div className="card p-8">
              <h3 className="text-xl font-display font-bold text-slate-900 dark:text-white mb-6">What we handle</h3>
              <ul className="space-y-4">
                {[
                  'Invoices and order info received via email (PDF and other formats)',
                  'Data entered into your CimmpleFlow and/or CimmplePay account',
                  'No data entry for you—focus on core operations',
                  'Scope and pricing tailored to your subscription(s)',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center bg-primary-100 dark:bg-primary-500/20 text-primary-600 dark:text-primary-400">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <span className="text-slate-600 dark:text-dark-300">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-20 bg-slate-50 dark:bg-dark-900/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="section-title">
              How it <span className="gradient-text">works</span>
            </h2>
            <p className="section-subtitle mx-auto mt-4">
              A simple, secure process so your data is always up to date.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: '1', title: 'You send documents', description: 'Forward invoices, orders, and other documents to our dedicated email. PDFs and common formats are supported.' },
              { step: '2', title: 'We enter the data', description: 'Our back-office team enters all information into your CimmpleFlow and/or CimmplePay subscription accurately and on schedule.' },
              { step: '3', title: 'You stay focused', description: 'Your system is up to date without you lifting a finger. Spend your time on what matters most—your core operations.' },
            ].map((item) => (
              <div key={item.step} className="card text-center">
                <div className="text-4xl font-display font-bold text-primary-500/30 mb-4">{item.step}</div>
                <h3 className="text-lg font-display font-bold text-slate-900 dark:text-white mb-2">{item.title}</h3>
                <p className="text-slate-600 dark:text-dark-400 text-sm">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* For which products */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="section-title">
              Available for your <span className="gradient-text">Cimmple</span> products
            </h2>
            <p className="section-subtitle mx-auto mt-4">
              Add back-office data entry for one or both of your subscriptions.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <Link to="/products/cimmpleflow" className="card group hover:border-primary-500/50 flex items-center gap-6 p-6">
              <div className="w-14 h-14 rounded-2xl bg-primary-100 dark:bg-primary-500/10 text-primary-600 dark:text-primary-400 flex items-center justify-center flex-shrink-0">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-display font-bold text-slate-900 dark:text-white group-hover:text-primary-600 dark:group-hover:text-primary-400">CimmpleFlow</h3>
                <p className="text-slate-600 dark:text-dark-400 text-sm">Orders, invoices, purchasing, and related data entry.</p>
              </div>
            </Link>
            <Link to="/products/cimmlepay" className="card group hover:border-accent-500/50 flex items-center gap-6 p-6">
              <div className="w-14 h-14 rounded-2xl bg-accent-100 dark:bg-accent-500/10 text-accent-600 dark:text-accent-400 flex items-center justify-center flex-shrink-0">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-display font-bold text-slate-900 dark:text-white group-hover:text-accent-600 dark:group-hover:text-accent-400">CimmplePay</h3>
                <p className="text-slate-600 dark:text-dark-400 text-sm">Payroll-related documents and data entry.</p>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Pricing CTA */}
      <section className="py-20 bg-slate-50 dark:bg-dark-900/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="card p-12 text-center relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-primary-500/10 to-accent-500/10" />
            <div className="relative">
              <h2 className="text-3xl md:text-4xl font-display font-bold text-slate-900 dark:text-white mb-4">
                Pricing tailored to your volume
              </h2>
              <p className="text-slate-700 dark:text-dark-300 text-lg mb-8 max-w-xl mx-auto">
                Back-office data entry is a paid add-on. Tell us which product you use (CimmpleFlow, CimmplePay, or both) and your typical volume—we'll provide a quote.
              </p>
              <Link to="/contact" className="btn-primary">
                Contact us for a quote
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BackOffice;
